import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, useAuth } from '../store/auth.store'
import { useImpersonation } from '../store/impersonation.store'
import type { Email, PaginatedResponse } from '../types'

const LIST_REFETCH_MS = 15_000

export function useEmails(params?: { page?: number; limit?: number; search?: string; status?: string; label?: string; criticidad?: string; hasCase?: boolean; orderBy?: string }) {
  const api = useApi()
  const { impersonateMailboxId } = useImpersonation()
  const { user } = useAuth()
  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.set('page', String(params.page))
  if (params?.limit) searchParams.set('limit', String(params.limit))
  if (params?.search) searchParams.set('search', params.search)
  if (params?.status) searchParams.set('status', params.status)
  if (params?.label) searchParams.set('label', params.label)
  if (params?.criticidad) searchParams.set('criticidad', params.criticidad)
  if (params?.hasCase) searchParams.set('hasCase', String(params.hasCase))
  if (params?.orderBy) searchParams.set('orderBy', params.orderBy)
  if (impersonateMailboxId) searchParams.set('mailboxId', impersonateMailboxId)
  const qs = searchParams.toString()
  return useQuery<PaginatedResponse<Email>>({
    queryKey: ['emails', user?.id, params, impersonateMailboxId],
    queryFn: () => api.get<PaginatedResponse<Email>>(`/emails${qs ? `?${qs}` : ''}`),
    refetchInterval: LIST_REFETCH_MS,
    refetchIntervalInBackground: false,
  })
}

export function useEmail(id: string) {
  const api = useApi()
  return useQuery<Email>({
    queryKey: ['email', id],
    queryFn: () => api.get<Email>(`/emails/${id}`),
    enabled: !!id,
  })
}

export function useEmailStats() {
  const api = useApi()
  const { impersonateMailboxId } = useImpersonation()
  const { user } = useAuth()
  const qs = impersonateMailboxId ? `?mailboxId=${impersonateMailboxId}` : ''
  return useQuery({
    queryKey: ['email-stats', user?.id, impersonateMailboxId],
    queryFn: () => api.get(`/emails/stats${qs}`),
    refetchInterval: 30000, // Refetch cada 30 segundos para unread count fresco
  })
}

export function useProcessEmail() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ emailId, force }: { emailId: string; force?: boolean }) => api.post(`/emails/${emailId}/process`, { force }),
    onSuccess: (_data, { emailId }) => {
      qc.invalidateQueries({ queryKey: ['email', emailId] })
      qc.invalidateQueries({ queryKey: ['emails'] })
      qc.invalidateQueries({ queryKey: ['cases'] })
      qc.invalidateQueries({ queryKey: ['alerts'] })
    },
  })
}

export function useGenerateResponse() {
  const api = useApi()
  return useMutation({
    mutationFn: ({ emailId, tone, customInstructions }: { emailId: string; tone?: string; customInstructions?: string }) =>
      api.post(`/emails/${emailId}/generate-response`, { tone, customInstructions }),
  })
}

export function useSendResponse() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      emailId,
      body,
      attachments,
    }: {
      emailId: string
      body: string
      attachments?: Array<{ id: string; url: string; filename: string; mimeType: string; size: number; localPath: string }>
    }) => api.post(`/emails/${emailId}/send-response`, { body, attachments }),
    onSuccess: (_data, { emailId }) => {
      qc.invalidateQueries({ queryKey: ['email', emailId] })
      qc.invalidateQueries({ queryKey: ['emails'] })
    },
  })
}

export function useMarkAsRead() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<unknown, Error, string, { previousEmailsQueries: [any, any][]; previousEmailDetail: any }>({
    mutationFn: (emailId: string) => api.patch(`/emails/${emailId}/read`, {}),
    onMutate: async (emailId) => {
      await qc.cancelQueries({ queryKey: ['emails'] })
      await qc.cancelQueries({ queryKey: ['email', emailId] })

      const previousEmailsQueries = qc.getQueriesData<any>({ queryKey: ['emails'] })
      const previousEmailDetail = qc.getQueryData<any>(['email', emailId])

      qc.setQueriesData<any>({ queryKey: ['emails'] }, (old) => {
        if (!old?.data || !Array.isArray(old.data)) return old
        return {
          ...old,
          data: old.data.map((e: any) =>
            e.id === emailId ? { ...e, isRead: true, readAt: new Date().toISOString() } : e,
          ),
        }
      })

      if (previousEmailDetail) {
        qc.setQueryData<any>(['email', emailId], (old: any) =>
          old ? { ...old, isRead: true, readAt: new Date().toISOString() } : old,
        )
      }

      return { previousEmailsQueries, previousEmailDetail }
    },
    onError: (_err, emailId, context) => {
      if (context?.previousEmailsQueries) {
        context.previousEmailsQueries.forEach(([key, data]) => {
          qc.setQueryData(key, data)
        })
      }
      if (context?.previousEmailDetail) {
        qc.setQueryData(['email', emailId], context.previousEmailDetail)
      }
    },
    onSettled: (_data, _err, emailId) => {
      qc.invalidateQueries({ queryKey: ['emails'] })
      qc.invalidateQueries({ queryKey: ['email', emailId] })
      qc.invalidateQueries({ queryKey: ['email-stats'] })
    },
  })
}

export function useMarkAsUnread() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<unknown, Error, string, { previousEmailsQueries: [any, any][]; previousEmailDetail: any }>({
    mutationFn: (emailId: string) => api.patch(`/emails/${emailId}/unread`, {}),
    onMutate: async (emailId) => {
      await qc.cancelQueries({ queryKey: ['emails'] })
      await qc.cancelQueries({ queryKey: ['email', emailId] })

      const previousEmailsQueries = qc.getQueriesData<any>({ queryKey: ['emails'] })
      const previousEmailDetail = qc.getQueryData<any>(['email', emailId])

      qc.setQueriesData<any>({ queryKey: ['emails'] }, (old) => {
        if (!old?.data || !Array.isArray(old.data)) return old
        return {
          ...old,
          data: old.data.map((e: any) =>
            e.id === emailId ? { ...e, isRead: false, readAt: null } : e,
          ),
        }
      })

      if (previousEmailDetail) {
        qc.setQueryData<any>(['email', emailId], (old: any) =>
          old ? { ...old, isRead: false, readAt: null } : old,
        )
      }

      return { previousEmailsQueries, previousEmailDetail }
    },
    onError: (_err, emailId, context) => {
      if (context?.previousEmailsQueries) {
        context.previousEmailsQueries.forEach(([key, data]) => {
          qc.setQueryData(key, data)
        })
      }
      if (context?.previousEmailDetail) {
        qc.setQueryData(['email', emailId], context.previousEmailDetail)
      }
    },
    onSettled: (_data, _err, emailId) => {
      qc.invalidateQueries({ queryKey: ['emails'] })
      qc.invalidateQueries({ queryKey: ['email', emailId] })
      qc.invalidateQueries({ queryKey: ['email-stats'] })
    },
  })
}

export function useToggleStarEmail() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<
    { id: string; isStarred: boolean },
    Error,
    { id: string; isStarred: boolean },
    { previousEmailsQueries: [any, any][]; previousEmailDetail: any }
  >({
    mutationFn: ({ id }) => api.patch(`/emails/${id}/star`, {}),
    onMutate: async ({ id, isStarred }) => {
      await qc.cancelQueries({ queryKey: ['emails'] })
      await qc.cancelQueries({ queryKey: ['email', id] })

      const previousEmailsQueries = qc.getQueriesData<any>({ queryKey: ['emails'] })
      const previousEmailDetail = qc.getQueryData<any>(['email', id])

      qc.setQueriesData<any>({ queryKey: ['emails'] }, (old) => {
        if (!old?.data || !Array.isArray(old.data)) return old
        return {
          ...old,
          data: old.data.map((e: any) =>
            e.id === id ? { ...e, isStarred: !isStarred } : e,
          ),
        }
      })

      if (previousEmailDetail) {
        qc.setQueryData<any>(['email', id], (old: any) =>
          old ? { ...old, isStarred: !isStarred } : old,
        )
      }

      return { previousEmailsQueries, previousEmailDetail }
    },
    onError: (_err, { id }, context) => {
      if (context?.previousEmailsQueries) {
        context.previousEmailsQueries.forEach(([key, data]) => {
          qc.setQueryData(key, data)
        })
      }
      if (context?.previousEmailDetail) {
        qc.setQueryData(['email', id], context.previousEmailDetail)
      }
    },
    onSettled: (_data, _err, { id }) => {
      qc.invalidateQueries({ queryKey: ['email', id] })
      qc.invalidateQueries({ queryKey: ['emails'] })
    },
  })
}

export function useUpdateCriticidad() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<
    { id: string; criticidad: string; caseId?: string | null; case?: any },
    Error,
    { emailId: string; criticidad: string; reason?: string },
    { previousEmailsQueries: [any, any][]; previousEmailDetail: any }
  >({
    mutationFn: ({ emailId, criticidad, reason }) =>
      api.patch(`/emails/${emailId}/criticidad`, { criticidad, reason }),
    onMutate: async ({ emailId, criticidad }) => {
      await qc.cancelQueries({ queryKey: ['emails'] })
      await qc.cancelQueries({ queryKey: ['email', emailId] })

      const previousEmailsQueries = qc.getQueriesData<any>({ queryKey: ['emails'] })
      const previousEmailDetail = qc.getQueryData<any>(['email', emailId])

      qc.setQueriesData<any>({ queryKey: ['emails'] }, (old) => {
        if (!old?.data || !Array.isArray(old.data)) return old
        return {
          ...old,
          data: old.data.map((e: any) =>
            e.id === emailId ? { ...e, criticidad } : e,
          ),
        }
      })

      if (previousEmailDetail) {
        qc.setQueryData<any>(['email', emailId], (old: any) =>
          old ? { ...old, criticidad } : old,
        )
      }

      return { previousEmailsQueries, previousEmailDetail }
    },
    onError: (_err, { emailId }, context) => {
      if (context?.previousEmailsQueries) {
        context.previousEmailsQueries.forEach(([key, data]) => {
          qc.setQueryData(key, data)
        })
      }
      if (context?.previousEmailDetail) {
        qc.setQueryData(['email', emailId], context.previousEmailDetail)
      }
    },
    onSettled: (_data, _err, { emailId }) => {
      qc.invalidateQueries({ queryKey: ['email', emailId] })
      qc.invalidateQueries({ queryKey: ['emails'] })
      qc.invalidateQueries({ queryKey: ['email-stats'] })
      qc.invalidateQueries({ queryKey: ['unified-labels'] })
      qc.invalidateQueries({ queryKey: ['cases'], exact: false })
      qc.invalidateQueries({ queryKey: ['case'], exact: false })
      qc.invalidateQueries({ queryKey: ['case-stats'], exact: false })
      qc.invalidateQueries({ queryKey: ['dashboard'], exact: false })
      qc.invalidateQueries({ queryKey: ['alerts'], exact: false })
      qc.invalidateQueries({ queryKey: ['alertStats'], exact: false })
      qc.invalidateQueries({ queryKey: ['alert-stats'], exact: false })
    },
  })
}

export function useToggleEmailHandled() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<
    any,
    Error,
    { emailId: string; isHandled: boolean; reason?: string },
    { previousEmailsQueries?: any; previousEmailDetail?: any }
  >({
    mutationFn: ({ emailId, isHandled, reason }) =>
      api.patch(`/emails/${emailId}/handled`, { isHandled, reason }),
    onMutate: async ({ emailId, isHandled }) => {
      await qc.cancelQueries({ queryKey: ['emails'] })
      await qc.cancelQueries({ queryKey: ['email', emailId] })

      const previousEmailsQueries = qc.getQueriesData<any>({ queryKey: ['emails'] })
      const previousEmailDetail = qc.getQueryData<any>(['email', emailId])

      qc.setQueriesData<any>({ queryKey: ['emails'] }, (old) => {
        if (!old?.data || !Array.isArray(old.data)) return old
        return {
          ...old,
          data: old.data.map((e: any) =>
            e.id === emailId ? { ...e, isHandled, handledAt: isHandled ? new Date().toISOString() : null } : e,
          ),
        }
      })

      if (previousEmailDetail) {
        qc.setQueryData<any>(['email', emailId], (old: any) =>
          old ? { ...old, isHandled, handledAt: isHandled ? new Date().toISOString() : null } : old,
        )
      }

      return { previousEmailsQueries, previousEmailDetail }
    },
    onError: (_err, { emailId }, context) => {
      if (context?.previousEmailsQueries) {
        context.previousEmailsQueries.forEach(([key, data]) => {
          qc.setQueryData(key, data)
        })
      }
      if (context?.previousEmailDetail) {
        qc.setQueryData(['email', emailId], context.previousEmailDetail)
      }
    },
    onSettled: (_data, _err, { emailId }) => {
      qc.invalidateQueries({ queryKey: ['email', emailId] })
      qc.invalidateQueries({ queryKey: ['emails'] })
      qc.invalidateQueries({ queryKey: ['email-stats'] })
      qc.invalidateQueries({ queryKey: ['cases'], exact: false })
      qc.invalidateQueries({ queryKey: ['case'], exact: false })
      qc.invalidateQueries({ queryKey: ['case-stats'], exact: false })
      qc.invalidateQueries({ queryKey: ['dashboard'], exact: false })
    },
  })
}

export function useGmailSync() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (mailboxId?: string | null) => {
      if (mailboxId) {
        return api.post(`/email-sync/${mailboxId}`, {})
      }
      return api.post('/email-sync/all', {})
    },
    onSuccess: () => {
      const keys = [
        'emails',
        'email-stats',
        'cases',
        'case-stats',
        'alerts',
        'alertStats',
        'dashboard',
        'unified-labels',
        'email-groups',
        'gmail-labels',
      ]
      keys.forEach((key) => qc.invalidateQueries({ queryKey: [key] }))
    },
  })
}

export function useSyncReadStatus() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (mailboxId?: string) => {
      const qs = mailboxId ? `?mailboxId=${mailboxId}` : ''
      return api.post(`/admin/sync-read-status${qs}`, {})
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['emails'] })
      qc.invalidateQueries({ queryKey: ['email-stats'] })
    },
  })
}

export function useGmailStatus() {
  const api = useApi()
  return useQuery({
    queryKey: ['gmail-status'],
    queryFn: () => api.get('/gmail/status'),
  })
}

export function useCreateCaseFromEmail() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (emailId: string) => api.post(`/emails/${emailId}/create-case`, {}),
    onSuccess: (_data, emailId) => {
      qc.invalidateQueries({ queryKey: ['email', emailId] })
      qc.invalidateQueries({ queryKey: ['emails'] })
      qc.invalidateQueries({ queryKey: ['cases'] })
    },
  })
}

export function useDeleteEmail() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ emailId, fromGmail = false }: { emailId: string; fromGmail?: boolean }) =>
      api.delete(`/emails/${emailId}?gmail=${fromGmail}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['emails'] })
      qc.invalidateQueries({ queryKey: ['email-stats'] })
    },
  })
}

export function useReplyEmail() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      emailId,
      to,
      subject,
      body,
      htmlBody,
      cc,
      bcc,
      attachments,
    }: {
      emailId: string
      to?: string[]
      subject?: string
      body: string
      htmlBody?: string
      cc?: string[]
      bcc?: string[]
      attachments?: Array<{ id: string; url: string; filename: string; mimeType: string; size: number; localPath: string }>
    }) => api.post(`/emails/${emailId}/reply`, { to, subject, body, htmlBody, cc, bcc, attachments }),
    onSuccess: (_data, { emailId }) => {
      qc.invalidateQueries({ queryKey: ['email', emailId] })
      qc.invalidateQueries({ queryKey: ['emails'] })
    },
  })
}

export function useReplySignature(emailId: string, enabled = true) {
  const api = useApi()
  return useQuery<{ html: string; source: 'gmail' | 'app' }>({
    queryKey: ['email-reply-signature', emailId],
    queryFn: () => api.get(`/emails/${emailId}/reply-signature`),
    enabled: !!emailId && enabled,
    staleTime: 5 * 60_000,
  })
}

export function useMySignature(mailboxId?: string | null, enabled = true) {
  const api = useApi()
  return useQuery<{ html: string; source: 'gmail' | 'app' }>({
    queryKey: ['my-email-signature', mailboxId],
    queryFn: () => mailboxId
      ? api.get(`/emails/my-signature?mailboxId=${mailboxId}`)
      : api.get('/emails/my-signature'),
    enabled,
    staleTime: 5 * 60_000,
  })
}

export function useGmailLabels() {
  const api = useApi()
  const { impersonateMailboxId } = useImpersonation()
  const { user } = useAuth()
  const qs = impersonateMailboxId ? `?mailboxId=${impersonateMailboxId}` : ''
  return useQuery<{ labels: { id: string; name: string; type?: string }[] }>({
    queryKey: ['gmail-labels', user?.id, impersonateMailboxId],
    queryFn: () => api.get(`/emails/labels/list${qs}`),
  })
}

export interface UnifiedLabel {
  id: string
  name: string
  type?: 'system' | 'user'
  color?: { backgroundColor?: string; textColor?: string }
  source: 'gmail' | 'db' | 'inferred'
  messageCount: number
  prefix?: 'MCI' | 'AI' | 'IA' | 'CLASIFICACION' | 'CRITICIDAD' | 'ACCION' | 'ENTIDAD' | 'AREA' | 'CASO' | 'ALERTA' | 'CUSTOM' | 'SYSTEM'
  entity?: string
  riskLevel?: string
  classification?: string
  action?: string
  area?: string
  aiPriority?: string
  confidence?: string
  caseId?: string
  alertId?: string
  syncedInMailboxes?: number
  totalMailboxes?: number
}

export interface LabelDeleteResult {
  ok: boolean
  deleted: number
  gmailDeleted: number
  errors: string[]
}

/**
 * Hook unificado de labels: devuelve TODOS los labels que el usuario puede usar
 * (los de Gmail del mailbox + los de BD que están aplicados a emails).
 * Devuelve labels planos + agrupados por taxonomía v2:
 *  criticidad, custom
 */
export function useUnifiedLabels() {
  const api = useApi()
  const { impersonateMailboxId } = useImpersonation()
  const { user } = useAuth()
  const qs = impersonateMailboxId ? `?mailboxId=${impersonateMailboxId}` : ''
  return useQuery<{
    labels: UnifiedLabel[]
    grouped: {
      criticidad: UnifiedLabel[]
      custom: UnifiedLabel[]
    }
    total: number
  }>({
    queryKey: ['unified-labels', user?.id, impersonateMailboxId],
    queryFn: () => api.get(`/emails/labels/unified${qs}`),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
}

export function useUpdateEmailLabels() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ emailId, addLabels, removeLabels }: { emailId: string; addLabels?: string[]; removeLabels?: string[] }) =>
      api.patch(`/emails/${emailId}/labels`, { addLabels: addLabels || [], removeLabels: removeLabels || [] }),
    onSuccess: (_data, { emailId }) => {
      qc.invalidateQueries({ queryKey: ['email', emailId] })
      qc.invalidateQueries({ queryKey: ['emails'] })
      qc.invalidateQueries({ queryKey: ['unified-labels'] })
    },
  })
}

export function useCreateLabel() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => api.post('/emails/labels', { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gmail-labels'] })
      qc.invalidateQueries({ queryKey: ['unified-labels'] })
    },
  })
}

export function useDeleteLabel() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<LabelDeleteResult, Error, string>({
    mutationFn: (name: string) => api.delete(`/emails/labels/${encodeURIComponent(name)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gmail-labels'] })
      qc.invalidateQueries({ queryKey: ['unified-labels'] })
      qc.invalidateQueries({ queryKey: ['emails'] })
    },
  })
}

export function usePruneEmptyLabels() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<{ ok: boolean; removed: string[] }>({
    mutationFn: () => api.post('/emails/labels/prune-empty', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['unified-labels'] }),
  })
}

export interface EmailGroup {
  label: string
  count: number
  unread: number
  entityName?: string
  riskLevel?: string
}

export function useEmailGroups() {
  const api = useApi()
  const { impersonateMailboxId } = useImpersonation()
  const { user } = useAuth()
  const qs = impersonateMailboxId ? `?mailboxId=${impersonateMailboxId}` : ''
  return useQuery<{ mci: EmailGroup[]; ai: EmailGroup[]; totalEmails: number }>({
    queryKey: ['email-groups', user?.id, impersonateMailboxId],
    queryFn: () => api.get(`/emails/groups${qs}`),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
}
